/**
 * Pricing-page content (hero + tiles + comparison table + FAQ),
 * kept local so it's easy to read, version, and tweak without
 * touching Strapi. Numbers MUST stay in sync with the BE catalog at
 * `aldo/src/const/plan-limits.ts`. Marketing language is here;
 * enforcement is over there.
 *
 * Shape mirrors the existing GraphQL `plan` query so the PlansTiles,
 * PlansTable, and FaqCategory components don't need to change. If
 * the marketing team decides this should live in the CMS later,
 * swap back is a one-line change in Plans.vue.
 *
 * Locale-keyed at the top (en-US / fr-FR). Adding a third language:
 * copy a block, translate, that's it.
 *
 * MVP scope (no permanence layer; physical letters paid per-use only;
 * recipient cap is the main upgrade lever): see the in-line
 * comments at each tile for the brand thesis per tier.
 */

const enUS = {
  // Hero: no overlay text on purpose. The background image (still
  // sourced from Strapi via apollo, see Plans.vue) carries the mood;
  // the tiles below speak for themselves. Title/description left
  // empty here so the Teaser inside BackgroundImageParallax renders
  // nothing on top of the image.
  hero: {
    title: 'Plans.',
    description: 'Free is free. The rest costs about a coffee.',
    img: { url: null },
  },

  // Three tiles at the top. Tier names + irreverent subPrice copy
  // ("You deserve it.", "After all, why not.") keep the brand voice.
  // Feature bullets per tile are the headline 4-6, not exhaustive.
  // the full breakdown lives in the comparison table below.
  priceTiles: {
    titles: { title: 'Pick the one that fits.' },
    feature_tiles: [
      {
        title: 'Free',
        // Tile title already says "Free", so the price slot just
        // carries the permanence cue ("Forever"). priceSmall:true
        // dials the font size down. it's a label, not a number,
        // and shouldn't compete visually with €1 / €99 on the
        // other two tiles.
        price: 'Forever',
        priceSmall: true,
        subPrice: 'Just in case.',
        feature_categories: [
          { label: 'Public profile' },
          { label: 'Private profile' },
          { label: '1 recipient per wish' },
        ],
        button: { url: '/hello', label: "Let's go", style: '' },
        icon: { url: null },
      },
      {
        title: 'Super Bzioup',
        price: '€0.99',
        priceSuffix: '/month',
        // Yearly truth (Apple price tier, mirrored on Google).
        yearlyPrice: '€9.99',
        yearlyPriceSuffix: '/year',
        subPrice: 'You deserve it.',
        // popular: highlights this tile as the recommended choice.
        // Drives a soft pink-tinted card + a labeled pill in
        // PlansTiles. Only ONE tile should have this true.
        // popularLabel: the pill text. Deliberately mocks the
        // "Popular" / "Best seller" / "Customer's choice" trope.
        popular: true,
        popularLabel: 'Cheaper than a coffee',
        popularLabelYearly: 'Cheaper than 10 coffees',
        feature_categories: [
          { label: 'Public profile' },
          { label: 'Private profile' },
          { label: '3 recipients per wish' },
          { label: 'Scheduled messages' },
          { label: 'Support' },
          // Marketing label for the activity-tracking + inactivity-push
          // feature. Deliberately mysterious. the FAQ does the
          // explanatory work. See open-threads memory item #13.
          { label: 'Auto-check' },
        ],
        button: { url: '/hello', label: "Let's go", style: '' },
        icon: { url: null },
      },
      {
        title: 'Master Bzioup',
        price: '€29.99',
        priceSuffix: '/month',
        yearlyPrice: '€289.99',
        yearlyPriceSuffix: '/year',
        subPrice: 'After all, why not.',
        // premium: flips the tile to a soft gold treatment in
        // PlansTiles (gold border + cream-tinted background + gold
        // accent on the price). Only ONE tile should have this true.
        // premiumLabel: rendered as a gold pill in the same slot
        // as popularLabel on the Super tile. Same self-aware
        // humor (mocks the price by comparing it to something
        // small + everyday).
        premium: true,
        feature_categories: [
          { label: 'Public profile' },
          { label: 'Private profile' },
          { label: 'Unlimited recipients' },
          { label: 'Scheduled messages' },
          { label: 'Support' },
          { label: 'Enhanced auto-check' },
          // Master-exclusive: worldwide visibility. the subscriber
          // (or their chosen words) appears on the public wall
          // visible to everyone, not just to their named recipients.
          // Master is the only tier that breaks out of the
          // "circle of named recipients" model.
          { label: 'World visibility' },
        ],
        button: { url: '/hello', label: "Let's go", style: '' },
        icon: { url: null },
      },
    ],
  },

  // Comparison table. Rows grouped by category. `advanced` is the
  // per-column value: "true" renders a check icon, any other string
  // renders the string, empty string renders nothing (visual gap =
  // not available on that tier).
  // Comparison table. Deliberately trimmed for the marketing page.
  // Every row is the user's actual upgrade decision; granular per-wish
  // breakdowns (body/clothes/music/notes individually) live in the
  // features page, not here. Same goes for physical letters. they get
  // a single line in the FAQ instead of their own table section.
  featuresTable: {
    titles: { title: 'In detail.' },
    feature_tiles: [
      { title: 'Free' },
      { title: 'Super' },
      { title: 'Master' },
    ],
    feature_categories: [
      {
        label: 'Public profile',
        features: [
          { label: 'Body', advanced: ['true', 'true', 'true'] },
          { label: 'Clothes', advanced: ['true', 'true', 'true'] },
          { label: 'Music', advanced: ['true', 'true', 'true'] },
          { label: 'Open notes', advanced: ['true', 'true', 'true'] },
          { label: 'Toggle visibility', advanced: ['true', 'true', 'true'] },
        ],
      },
      {
        label: 'Private profile',
        description: 'Released only when the time comes.',
        features: [
          { label: 'One video', advanced: ['true', 'true', 'true'] },
          { label: 'One letter', advanced: ['true', 'true', 'true'] },
          { label: 'Accounts manager', advanced: ['3', '20', '99'] },
          { label: 'Scheduled messages', advanced: ['', '20', '99'] },
          /* Cross-cutting cap on every private wish above. lives in
             this section now (instead of its own "Recipients"
             section) since the recipient count IS the per-wish
             property that differentiates the tiers most. */
          { label: 'Recipients per wish', advanced: ['1', '3', 'Unlimited'] },
          /* Visibility scope of a wish when it triggers:
             - Free: blank → named recipients only (the default)
             - Super: "Circle" → broadcast to the user's bzioup
                                 circle (personalFeed in the BE)
             - Master: "Worldwide" → broadcast to the public main
                                     feed too (mainFeed in the BE) */
          { label: 'Visibility', advanced: ['', 'Circle', 'Worldwide'] },
        ],
      },
      {
        label: 'Support & auto-check',
        features: [
          { label: 'Support', advanced: ['', 'Email', 'Email and Phone'] },
          { label: 'Auto-check', description: 'Not sure what it is about yet.', advanced: ['', 'Basic', 'Advanced'] },
        ],
      },
    ],
  },

  // FAQ. The first question is load-bearing. it is the silent worry
  // every visitor of a death product brings to the pricing page, and
  // answering it directly is the conversion unlock. Keep that one as
  // item [0].
  questions: {
    items: [
      {
        category: 'About your subscription',
        icon: { url: null },
        questions: [
          {
            question: 'What is "Public profile"?',
            answer:
              "The 'Public Profile' is a dedicated page for non-sensitive information. Visible by default, it allows you to share the details you choose to make publicly accessible, safely and on your terms. In return, you can also explore other users' public profiles.",
          },
          {
            question: 'What is "Private profile"?',
            answer:
              "The Private Profile is your confidential space for personal and sensitive information. It stays hidden while you are still present and becomes accessible to your chosen contacts only when the time comes. Likewise, you may access another's private profile when the time comes.",
          },
          {
            question: 'Can I cancel anytime?',
            answer:
              "Yes. You keep access to everything you set up; you just can't add or edit until you resubscribe.",
          },
          {
            question: 'What happens to my content if I stop paying?',
            answer:
              "Anything you set up while subscribed stays. You can still log in, read what's there, and delete things you don't want. You won't be able to add new wishes or edit existing ones unless you resubscribe. When the time comes, everything you set up is delivered. That's the deal.",
          },
          {
            question: 'Will my wishes really be delivered?',
            answer:
              "That's the idea. We hold what you set up and deliver it to the recipients you chose. Read more on the features page and inside the app.",
          },
        ],
      },
    ],
  },
}

const frFR = {
  hero: {
    title: 'Tarifs.',
    description: "Gratuit, c'est gratuit. Le reste coûte à peu près une baguette.",
    img: { url: null },
  },

  priceTiles: {
    titles: { title: 'Il y en a pour tout le monde.' },
    feature_tiles: [
      {
        title: 'Free',
        price: 'Pour toujours',
        priceSmall: true,
        subPrice: 'Au cas où.',
        feature_categories: [
          { label: 'Profil public' },
          { label: 'Profil privé' },
          { label: '1 destinataire par souhait' },
        ],
        button: { url: '/hello', label: "Let's go", style: '' },
        icon: { url: null },
      },
      {
        title: 'Super Bzioup',
        price: '€0,99',
        priceSuffix: '/mois',
        yearlyPrice: '€9,99',
        yearlyPriceSuffix: '/an',
        subPrice: 'Vous le méritez.',
        popular: true,
        popularLabel: "Moins cher qu'une baguette de pain",
        popularLabelYearly: "Moins cher que 10 baguettes de pain",
        feature_categories: [
          { label: 'Profil public' },
          { label: 'Profil privé' },
          { label: '3 destinataires par souhait' },
          { label: 'Messages programmés' },
          { label: 'Support' },
          { label: 'Auto-veille' },
        ],
        button: { url: '/hello', label: "Let's go", style: '' },
        icon: { url: null },
      },
      {
        title: 'Master Bzioup',
        price: '€29,99',
        priceSuffix: '/mois',
        yearlyPrice: '€289,99',
        yearlyPriceSuffix: '/an',
        subPrice: 'Après tout, pourquoi pas.',
        premium: true,
        feature_categories: [
          { label: 'Profil public' },
          { label: 'Profil privé' },
          { label: 'Destinataires illimités' },
          { label: 'Messages programmés' },
          { label: 'Support' },
          { label: 'Auto-veille avancée' },
          { label: 'Visibilité mondiale' },
        ],
        button: { url: '/hello', label: "Let's go", style: '' },
        icon: { url: null },
      },
    ],
  },

  featuresTable: {
    titles: { title: 'Plus en détails.' },
    feature_tiles: [
      { title: 'Free' },
      { title: 'Super' },
      { title: 'Master' },
    ],
    feature_categories: [
      {
        label: 'Profil public',
        features: [
          { label: 'Corps', advanced: ['true', 'true', 'true'] },
          { label: 'Vêtements', advanced: ['true', 'true', 'true'] },
          { label: 'Musique', advanced: ['true', 'true', 'true'] },
          { label: 'Notes ouvertes', advanced: ['true', 'true', 'true'] },
          { label: 'Visibilité réglable', advanced: ['true', 'true', 'true'] },
        ],
      },
      {
        label: 'Profil privé',
        description: 'Révélé seulement le moment venu.',
        features: [
          { label: 'Une vidéo', advanced: ['true', 'true', 'true'] },
          { label: 'Une lettre', advanced: ['true', 'true', 'true'] },
          { label: 'Gestionnaire de comptes', advanced: ['3', '20', '99'] },
          { label: 'Messages programmés', advanced: ['', '20', '99'] },
          { label: 'Destinataires par souhait', advanced: ['1', '3', 'Illimité'] },
          { label: 'Visibilité', advanced: ['', 'Mon entourage', 'Mondiale'] },
        ],
      },
      {
        label: 'Support et auto-veille',
        features: [
          { label: 'Support', advanced: ['', 'Email', 'Email et téléphone'] },
          { label: 'Auto-veille', description: "Pas encore sûr de ce que c'est.", advanced: ['', 'Standard', 'Avancée'] },
        ],
      },
    ],
  },

  questions: {
    items: [
      {
        category: 'À propos de votre abonnement',
        icon: { url: null },
        questions: [
          {
            question: 'C\'est quoi le "Profil public" ?',
            answer:
              "Le 'Profil public' est une page dédiée aux informations non sensibles. Visible par défaut, il vous permet de partager les détails que vous choisissez de rendre publics, en toute sécurité et selon vos conditions. En retour, vous pouvez aussi explorer les profils publics des autres utilisateurs.",
          },
          {
            question: 'C\'est quoi le "Profil privé" ?',
            answer:
              "Le Profil privé est votre espace confidentiel pour vos informations personnelles et sensibles. Il reste caché tant que vous êtes là, et devient accessible à vos contacts choisis le moment venu. Réciproquement, vous pouvez accéder au profil privé en temps et en heure.",
          },
          {
            question: 'Puis-je annuler à tout moment ?',
            answer:
              "Oui. Vous gardez l'accès à tout ce que vous avez mis en place; vous ne pouvez simplement plus ajouter ni modifier sans vous réabonner.",
          },
          {
            question: "Que devient mon contenu si j'arrête de payer ?",
            answer:
              "Tout ce que vous avez mis en place pendant que vous étiez abonné reste. Vous pouvez toujours vous connecter, relire ce qui est là, et supprimer ce que vous voulez. Vous ne pourrez plus ajouter de nouveaux souhaits ni modifier les existants sans vous réabonner. Le moment venu, tout ce que vous avez mis en place est livré. C'est notre engagement.",
          },
          {
            question: 'Mes souhaits seront-ils vraiment livrés ?',
            answer:
              "C'est l'idée. Nous gardons ce que vous avez mis en place et le transmettons aux destinataires que vous avez choisis. Plus de détails sur la page Fonctionnalités ainsi que dans l'application.",
          },
        ],
      },
    ],
  },
}

export const plansContent = {
  'en-US': enUS,
  'fr-FR': frFR,
}

/**
 * Picks the right slice for the active locale. Falls back to en-US
 * if the language is unset or unknown. The store's `language` value
 * is the BCP-47 code Strapi uses, so 'en-US' / 'fr-FR'.
 */
export function getPlansContent(language) {
  return plansContent[language] || plansContent['en-US']
}
